"use client";

import { useEffect, useRef, useState } from "react";
import {
    TURNSTILE_SITE_KEY,
    isCaptchaEnabledOnClient,
} from "@/app/libs/captcha-config";

type CaptchaWidgetProps = {
    onTokenChange: (token: string | null) => void;
    resetSignal?: number;
    size?: "normal" | "compact" | "flexible";
    appearance?: "always" | "execute" | "interaction-only";
    label?: string;
    helperText?: string;
    className?: string;
    labelClassName?: string;
    helperTextClassName?: string;
    errorClassName?: string;
};

const LOAD_ERROR_MESSAGE =
    "Spam protection could not load. Refresh the page and try again.";

export function CaptchaWidget({
    onTokenChange,
    resetSignal = 0,
    size = "normal",
    appearance = "always",
    label = "Spam protection",
    helperText = "Complete the check before you submit the form.",
    className,
    labelClassName,
    helperTextClassName,
    errorClassName,
}: CaptchaWidgetProps) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const widgetIdRef = useRef<string | null>(null);
    const [loadError, setLoadError] = useState("");

    useEffect(() => {
        onTokenChange(null);

        if (!isCaptchaEnabledOnClient || !containerRef.current) {
            setLoadError("");
            return;
        }

        let attempts = 0;
        let stopped = false;
        let intervalId: number | null = null;

        const renderWidget = () => {
            if (!containerRef.current || !window.turnstile) {
                return false;
            }

            if (widgetIdRef.current) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }

            containerRef.current.innerHTML = "";

            widgetIdRef.current = window.turnstile.render(
                containerRef.current,
                {
                    sitekey: TURNSTILE_SITE_KEY,
                    size,
                    appearance,
                    theme: "light",
                    callback: (token) => {
                        if (!stopped) {
                            setLoadError("");
                            onTokenChange(token);
                        }
                    },
                    "expired-callback": () => {
                        if (!stopped) {
                            onTokenChange(null);
                        }
                    },
                    "error-callback": () => {
                        if (!stopped) {
                            onTokenChange(null);
                            setLoadError(LOAD_ERROR_MESSAGE);
                        }
                    },
                }
            );

            setLoadError("");
            return true;
        };

        const tryRender = () => {
            if (renderWidget()) {
                if (intervalId !== null) {
                    window.clearInterval(intervalId);
                }
                return;
            }

            attempts += 1;
            if (attempts >= 80) {
                setLoadError(LOAD_ERROR_MESSAGE);
                if (intervalId !== null) {
                    window.clearInterval(intervalId);
                }
            }
        };

        tryRender();

        if (!widgetIdRef.current) {
            intervalId = window.setInterval(tryRender, 250);
        }

        return () => {
            stopped = true;
            if (intervalId !== null) {
                window.clearInterval(intervalId);
            }
            if (widgetIdRef.current && window.turnstile) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, [appearance, onTokenChange, resetSignal, size]);

    if (!isCaptchaEnabledOnClient) {
        return null;
    }

    return (
        <div className={className}>
            {label ? (
                <div
                    className={
                        labelClassName ?? "mb-2 text-sm font-semibold text-slate-900"
                    }
                >
                    {label}
                </div>
            ) : null}
            <div ref={containerRef} />
            {loadError || helperText ? (
                <p
                    className={
                        loadError
                            ? errorClassName ?? "mt-2 text-xs text-red-600"
                            : helperTextClassName ?? "mt-2 text-xs text-slate-500"
                    }
                    role={loadError ? "alert" : undefined}
                >
                    {loadError || helperText}
                </p>
            ) : null}
        </div>
    );
}
